import classNames from 'classnames';

import React, {
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useMemo,
} from 'react';

import { Counter } from '../counter';
import { Icon as IconComponent } from '../icon';
import { Image as ImageComponent } from '../image';
import { Typography } from '../typography';

import styles from './chip.scss';

type Props = RefAttributes<HTMLDivElement> &
  Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    value: string | number;
    checked?: boolean;
    className?: string;
    onChange?: (value: string | number, checked: boolean) => void;
    icon?: string | ReactElement;
    selectedIcon?: string;
    image?: string | ReactElement;
    selectedImage?: string;
    dataTestId?: string;
    disabled?: boolean;
    counter?: {
      type: 'error' | 'info' | 'default' | 'dot';
      value?: number;
    };
  };

const COMPONENT_ID = 'modulor-chip';

const Icon: FC<Pick<Props, 'icon' | 'dataTestId'>> = ({ icon, dataTestId }) => {
  if (icon) {
    return typeof icon === 'string' ? (
      <IconComponent name={icon} size={16} dataTestId={dataTestId} />
    ) : (
      icon
    );
  }
  return null;
};

const Image: FC<Pick<Props, 'image' | 'value' | 'dataTestId'>> = ({ image, value, dataTestId }) => {
  if (image) {
    return typeof image === 'string' ? (
      <ImageComponent
        name={image}
        width="16"
        height="16"
        alt={String(value)}
        dataTestId={dataTestId}
      />
    ) : (
      image
    );
  }
  return null;
};

export const Chip: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      checked,
      className,
      value,
      onChange,
      icon,
      selectedIcon,
      image,
      selectedImage,
      disabled,
      counter,
      children,
      dataTestId = COMPONENT_ID,
      ...rest
    },
    ref,
  ) => {
    const classes = classNames(styles.container, className, {
      [styles.active]: checked,
      [styles.labelHasIcon]: icon || image,
      [styles.disabled]: disabled,
    });

    const handleChange = () => onChange?.(value, checked);

    const counterProps = useMemo(() => {
      if (!counter) {
        return {};
      }

      const values = {
        error: '!',
        info: counter.value,
        default: counter.value,
        dot: '',
      };

      return {
        value: values[counter.type],
        className: classNames(styles.counter, styles[counter.type]),
      };
    }, [counter]);

    const resolvedIcon = useMemo(
      () => (checked && selectedIcon ? selectedIcon : icon),
      [checked, selectedIcon, icon],
    );

    const resolvedImage = useMemo(
      () => (checked && selectedImage ? selectedImage : image),
      [checked, selectedImage, image],
    );

    return (
      <div
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        data-id={dataTestId}
        className={classes}
        ref={ref}
        {...rest}>
        <label>
          <input
            type="checkbox"
            name="checkbox"
            value={value}
            checked={checked}
            onChange={handleChange}
            data-testid={`${dataTestId}-input`}
          />
          <Icon icon={resolvedIcon} dataTestId={`${dataTestId}-icon`} />
          <Image image={resolvedImage} value={value} dataTestId={`${dataTestId}-image`} />
          <Typography
            tag="p"
            dataTestId={`${dataTestId}-text`}
            variant={checked ? 'advanced-chips-selected' : 'advanced-chips-default'}>
            {children}
          </Typography>
          {counter && <Counter {...counterProps} />}
        </label>
      </div>
    );
  },
);

Chip.displayName = 'Chip';
